/**
 * reachability: entry-point — Sarah visual identity.
 * Matt approved public/images/sarah/avatar.jpg as the final avatar on
 * 2026-10-15. The launcher and Sarah's chat messages both use this path.
 * Swap the file only; do not inline a second photo.
 */
export const SARAH_AVATAR_SRC = '/images/sarah/avatar.jpg' as const
export const SARAH_AVATAR_SRC_2X = '/images/sarah/avatar@2x.jpg' as const
export const SARAH_AVATAR_SRC_3X = '/images/sarah/avatar@3x.jpg' as const
export const SARAH_AVATAR_SRCSET =
  `${SARAH_AVATAR_SRC} 1x, ${SARAH_AVATAR_SRC_2X} 2x, ${SARAH_AVATAR_SRC_3X} 3x` as const
export const SARAH_AVATAR_ALT = 'Sarah, Ryan Realty AI assistant' as const

/** Matt's public headshot, used only when he has taken over a chat. */
export const MATT_CHAT_PHOTO_SRC = '/images/brokers/ryan-matt.png' as const
export const MATT_CHAT_PHOTO_ALT = 'Matt Ryan' as const

/**
 * Animated launcher face (Matt 2026-10-26, "can she be animated like a video",
 * then "Just turn on. She should wave, smile, do the phone to ear sign").
 * Both clips are cut from avatar.jpg with image-to-video, framed a little wider
 * so her hands fit inside the disc, and start and end on the rest pose. The
 * gesture (smile, wave, call-me sign) plays once after idle and at most once
 * more per session; the idle loop (smile and blink) runs between. The still
 * stays the poster and the fallback. This flag turns both off.
 */
export const SARAH_AVATAR_VIDEO_ENABLED: boolean = true
export const SARAH_AVATAR_VIDEO_WEBM = '/images/sarah/loop/idle.webm' as const
export const SARAH_AVATAR_VIDEO_MP4 = '/images/sarah/loop/idle.mp4' as const
export const SARAH_AVATAR_GESTURE_WEBM = '/images/sarah/loop/gesture.webm' as const
export const SARAH_AVATAR_GESTURE_MP4 = '/images/sarah/loop/gesture.mp4' as const

/**
 * Event-driven launcher clips (Front Desk 2026-10-29, after Matt rejected idle
 * v2). Files live at /images/sarah/clips/<slot>-<size>.{webm,mp4}, size 240
 * (mobile) or 720 (desktop, served at 480 to 720px).
 * A slot is used only when listed in SARAH_CLIPS_AVAILABLE; anything missing,
 * or failing to load, falls back to the current idle loop above. Front Desk
 * checks every clip before a slot is listed here.
 */
export type SarahClipSize = 240 | 720
export const SARAH_CLIPS_AVAILABLE: ReadonlySet<string> = new Set<string>([])
export function sarahClipSources(
  slot: string,
  size: SarahClipSize,
): { webm: string; mp4: string } {
  if (slot === 'resting' && !SARAH_CLIPS_AVAILABLE.has('resting')) {
    return { webm: SARAH_AVATAR_VIDEO_WEBM, mp4: SARAH_AVATAR_VIDEO_MP4 }
  }
  if (!SARAH_CLIPS_AVAILABLE.has(slot)) return sarahClipSources('resting', size)
  const base = `/images/sarah/clips/${slot}-${size}`
  return { webm: `${base}.webm`, mp4: `${base}.mp4` }
}
/** 240 on phones (768px and below), 720 on desktop. dpr kept for a 480 tier. */
export function sarahClipSize(viewportWidth: number, dpr: number): SarahClipSize {
  void dpr
  return viewportWidth <= 768 ? 240 : 720
}
